/**
 * Pure drop-resolution for the swimlane board.
 *
 * The swimlane board is a 2D grid of cells keyed `${laneKey}${status}`.
 * A board drag may change a card's *status* (move across columns within its
 * lane) but must never change the *lane* dimension (assignee / priority /
 * milestone) — that's not what a drag means. This function takes the
 * pre-drag snapshot and the post-`onDragOver` items map and decides what (if
 * anything) to persist.
 *
 * Extracted from the component so the lane-constraint + neighbour derivation
 * is unit-testable without a dnd-kit context.
 */

// SYMBOL FOR START OF HEADING — cannot occur in any lane key (user/milestone/
// epic ids, priority ints, type slugs are all alnum/underscore).
export const CELL_SEP = "";

export type CellId = string;

export const makeCellId = (laneKey: string, status: string): CellId =>
  `${laneKey}${CELL_SEP}${status}`;

export function parseCellId(id: CellId): [laneKey: string, status: string] {
  const i = id.indexOf(CELL_SEP);
  return [id.slice(0, i), id.slice(i + 1)];
}

export const isCellId = (id: string): boolean => id.includes(CELL_SEP);

type MinItem = { id: string };
type CellMap<T extends MinItem> = Record<string, T[]>;

export type DropResolution =
  | { type: "noop" }
  | { type: "reject" }
  | {
      type: "move";
      status: string | null;
      prevId: string | null;
      nextId: string | null;
    };

function cellContaining<T extends MinItem>(
  map: CellMap<T>,
  id: string,
): string | null {
  for (const k of Object.keys(map))
    if ((map[k] ?? []).some((t) => t.id === id)) return k;
  return null;
}

export function resolveSwimlaneDrop<T extends MinItem>(
  snapshot: CellMap<T>,
  items: CellMap<T>,
  activeId: string,
): DropResolution {
  const destCell = cellContaining(items, activeId);
  const fromCell = cellContaining(snapshot, activeId);
  if (!destCell || !fromCell) return { type: "noop" };

  const [destLane, destStatus] = parseCellId(destCell);
  const [fromLane, fromStatus] = parseCellId(fromCell);

  // A card cannot leave its lane via a board drag.
  if (destLane !== fromLane) return { type: "reject" };

  const destList = items[destCell] ?? [];
  const destIdx = destList.findIndex((t) => t.id === activeId);
  const fromIdx = (snapshot[fromCell] ?? []).findIndex(
    (t) => t.id === activeId,
  );

  // No net change in position or status.
  if (fromStatus === destStatus && fromIdx === destIdx) return { type: "noop" };

  const prevId = destIdx > 0 ? destList[destIdx - 1]!.id : null;
  const nextId =
    destIdx < destList.length - 1 ? destList[destIdx + 1]!.id : null;

  return {
    type: "move",
    status: fromStatus !== destStatus ? destStatus : null,
    prevId,
    nextId,
  };
}
